import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { X, Clock, Loader2, ArrowDownWideNarrow, ArrowUpWideNarrow } from 'lucide-react';
import dayjs from 'dayjs';
import useVisitStats from './useVisitStats';
import useFeatures from '@/hooks/useFeatures';
import PeopleVisitsPanel from './PeopleVisitsPanel';

/**
 * Format duration in minutes as "Xh Ym" or "Ym".
 */
function formatDuration(minutes) {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export default function VisitHistoryDrawer({ poi, onClose, topOffset = 0 }) {
  const { t } = useTranslation();
  const devices = useSelector((state) => state.devices.items);
  const drivers = useSelector((state) => state.drivers.items);
  const [daysBack, setDaysBack] = useState(7);
  const [deviceFilter, setDeviceFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = newest first
  // People tab: GET /api/pois/:id/staff-visits is Workforce-gated and
  // admin/manager-only — hide the tab rather than show a 403.
  const { workforce } = useFeatures();
  const orgRole = useSelector((state) => state.org?.role);
  const isSultan = useSelector((state) => state.sultan?.isSultan);
  const canSeePeople = workforce && (isSultan || orgRole === 'admin' || orgRole === 'manager');
  const [tab, setTab] = useState('vehicles');
  const activeTab = canSeePeople ? tab : 'vehicles';

  // Convert integer days to { from, to } object for useVisitStats
  const dateRange = useMemo(() => ({
    from: dayjs().subtract(daysBack, 'day').toISOString(),
    to: dayjs().toISOString(),
  }), [daysBack]);

  const { visits, loading } = useVisitStats(poi, dateRange);

  const deviceName = (id) => devices[id]?.name || `#${id}`;
  const driverName = (deviceId) => {
    const device = devices[deviceId];
    if (!device) return '—';
    const driverObj = Object.values(drivers).find((d) => d.uniqueId === device.attributes?.driverUniqueId);
    return driverObj?.name || '—';
  };

  // Distinct device options derived from the loaded visits — keeps the
  // dropdown scoped to "things this POI has actually seen".
  const deviceOptions = useMemo(() => {
    const seen = new Map();
    for (const v of visits) {
      if (!seen.has(v.deviceId)) seen.set(v.deviceId, deviceName(v.deviceId));
    }
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [visits, devices]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredVisits = useMemo(() => {
    const filtered = deviceFilter === 'all'
      ? visits
      : visits.filter((v) => String(v.deviceId) === String(deviceFilter));
    // useVisitStats returns ascending (server ORDER BY fix_time ASC) — copy
    // before sort so we don't mutate the upstream array.
    const sorted = [...filtered].sort((a, b) =>
      dayjs(a.enterTime).valueOf() - dayjs(b.enterTime).valueOf());
    return sortOrder === 'desc' ? sorted.reverse() : sorted;
  }, [visits, deviceFilter, sortOrder]);

  if (!poi) return null;

  const sortButton = (
      <button
        type="button"
        onClick={() => setSortOrder((p) => (p === 'desc' ? 'asc' : 'desc'))}
        className="flex-shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        title={sortOrder === 'desc'
          ? t('pois.sortOldestFirst', 'Sort oldest first')
          : t('pois.sortNewestFirst', 'Sort newest first')}
      >
        {sortOrder === 'desc'
          ? <ArrowDownWideNarrow size={14} />
          : <ArrowUpWideNarrow size={14} />}
      </button>
  );

  return (
    <div
      className="absolute end-0 w-[400px] bg-white dark:bg-slate-900 border-s border-slate-200 dark:border-slate-800 shadow-xl dark:shadow-black/50 z-30 flex flex-col"
      style={{ top: topOffset, bottom: 0 }}
    >
      {/* Header */}
      <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-bold text-sm text-slate-800 dark:text-slate-200 truncate">{poi.name}</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
            <Clock size={11} /> {t('pois.visitHistory')}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 flex-shrink-0"
          title={t('pois.close', 'Close')}
        >
          <X size={16} />
        </button>
      </div>

      {canSeePeople ? (
        <div className="px-4 pt-2 flex gap-1 border-b border-slate-50 dark:border-slate-800" role="tablist">
          {[['vehicles', t('pois.tabVehicles', 'Vehicles')], ['people', t('pois.tabPeople', 'People')]].map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={activeTab === value}
              onClick={() => setTab(value)}
              className={`px-3 py-1.5 -mb-px text-xs font-semibold border-b-2 transition-colors ${
                activeTab === value ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {/* Date range selector */}
      <div className="px-4 py-2 border-b border-slate-50 dark:border-slate-800 flex gap-2">
        {[7, 14, 30].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDaysBack(d)}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
              daysBack === d ? 'bg-blue-500 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            {d}d
          </button>
        ))}
      </div>

      {activeTab === 'people' ? (
        <PeopleVisitsPanel poi={poi} dateRange={dateRange} sortOrder={sortOrder} sortButton={sortButton} />
      ) : (<>
      {/* Vehicle filter + sort toggle. Staff visits live on the People tab
          (/api/pois/:id/staff-visits — staff_positions, not `positions`). */}
      <div className="px-4 py-2 border-b border-slate-50 dark:border-slate-800 flex gap-2">
        <select
          value={deviceFilter}
          onChange={(e) => setDeviceFilter(e.target.value)}
          disabled={deviceOptions.length === 0}
          className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg text-xs bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-50"
        >
          <option value="all">{t('pois.allVehicles', 'All vehicles')}</option>
          {deviceOptions.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>
        {sortButton}
      </div>

      {/* Visit list */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={20} className="animate-spin text-slate-300" />
          </div>
        ) : filteredVisits.length === 0 ? (
          <div className="text-center py-12 text-slate-400 text-xs">{t('pois.noVisits')}</div>
        ) : (
          <div className="divide-y divide-slate-50 dark:divide-slate-800">
            {filteredVisits.map((visit, i) => (
              <div key={i} className="px-4 py-3 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-slate-700 dark:text-slate-300">{deviceName(visit.deviceId)}</span>
                  <span className="text-slate-400">{dayjs(visit.enterTime).format('MMM D')}</span>
                </div>
                <div className="flex items-center gap-4 text-slate-500">
                  <span>{t('pois.driver')}: {driverName(visit.deviceId)}</span>
                  <span>{t('pois.arrived')}: {dayjs(visit.enterTime).format('HH:mm')}</span>
                  <span>{t('pois.departed')}: {visit.exitTime ? dayjs(visit.exitTime).format('HH:mm') : '—'}</span>
                </div>
                <div className="mt-1 text-slate-600 dark:text-slate-400 font-medium">
                  {t('pois.duration')}: {formatDuration(visit.durationMinutes)}
                </div>
                {visit.positions?.length > 1 && (
                  <div className="mt-0.5 text-slate-400">
                    {visit.positions.length} {t('pois.positions', { defaultValue: 'positions' })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      </>)}
    </div>
  );
}
